/**
 * CellFkRowPicker — the `FkLookupPicker` dialog as a cell editor mounts it.
 *
 * Two editors open the full row picker from a cell: `FkCellEditor` (its whole
 * reason to exist) and `EnumCellEditor` (the "Search rows in …" escape a
 * declared lookup enum offers once it outranks the FK — see
 * `cellEditVariant.ts`). Both need the exact same wiring — the loose
 * declaration's `targetFilter` / `sourcePath`, the `preselectedLooseRelId` that
 * skips the picker's own multi-FK chooser, and the Clear-to-NULL rule — so it
 * lives here once instead of being copied into the second editor.
 *
 * The dialog must be mounted OUTSIDE the editor's popover: opening it closes
 * the popover, and a dialog rendered inside would unmount with it.
 */

import { FkLookupPicker } from '../../../table-explorer/components/FkLookupPicker';
import type { FkPickerTarget } from './FkCellEditor';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { TableExplorerRelationshipPort } from '../../../table-explorer/components/tableExplorerHostPorts';

export type CellFkRowPickerProps = {
  /** The FK target being searched. `looseRel` drives the loose-only props. */
  target: FkPickerTarget;
  connectionId: string;
  database: string;
  engine: string;
  /** Source column the cell belongs to — the picker's source context. */
  sourceSchema: string;
  sourceTable: string;
  sourceColumn: string;
  sourceDataType?: string;
  /** Current cell value — a null/undefined cell gets no "Clear" button. */
  initialValue: unknown;
  /** Host port (Azure Table): the picker reads through it, never SQL (M7). */
  relationshipPort?: TableExplorerRelationshipPort;
  /** Dialog dismissed without a pick. */
  onClose: () => void;
  /** A row was picked — the referenced column's value. */
  onPicked: (value: string | number | boolean | Date) => void;
  /** "Clear" pressed — the caller sets the cell to NULL and closes. */
  onClearToNull: () => void;
  /** The editor's input — focus returns to it when the picker closes. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
};

export function CellFkRowPicker({
  target,
  connectionId,
  database,
  engine,
  sourceSchema,
  sourceTable,
  sourceColumn,
  sourceDataType,
  initialValue,
  relationshipPort,
  onClose,
  onPicked,
  onClearToNull,
  returnFocusRef,
}: CellFkRowPickerProps) {
  return (
    <FkLookupPicker
      open={true}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      connectionId={connectionId}
      database={database}
      engine={engine as DatabaseEngineId}
      referencedSchema={target.schema}
      referencedTable={target.table}
      referencedColumn={target.column}
      sourceLabel={`Pick a ${sourceColumn}`}
      source={{
        schema: sourceSchema,
        table: sourceTable,
        column: sourceColumn,
        dataType: sourceDataType ?? '',
      }}
      targetFilter={target.looseRel?.targetFilter}
      sourcePath={target.looseRel?.sourcePath}
      // Explicit pre-selection: the editor already resolved which declaration
      // is being searched. Passing the id skips the picker's multi-FK chooser.
      preselectedLooseRelId={target.looseRel?.id}
      relationshipPort={relationshipPort}
      // A real-FK target (`cellEditVariant` puts the constraint first) wins
      // over any loose declaration the picker would resolve on the column.
      referencesRealForeignKey={!target.isLoose}
      onSelect={(value) => onPicked(value)}
      // Clear sets the cell to NULL. Only offered when the cell isn't already
      // null/empty — clearing-already-null is a no-op the user shouldn't have
      // to think about.
      onClear={
        initialValue !== null && initialValue !== undefined ? onClearToNull : undefined
      }
      returnFocusRef={returnFocusRef}
    />
  );
}
